import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { PanelTopHeader } from '@/components/panels/PanelTopHeader/PanelTopHeader';
import { PanelTopSearch } from '@/components/panels/PanelTopSearch/PanelTopSearch';
import { ScrollArea } from '@/components/ui/scroll-area';

import { SectionField } from '@/components/panels/sections/SectionField/SectionField';
import { SectionFieldSaved } from '@/components/panels/sections/SectionFieldSaved/SectionFieldSaved';

import IconAdd from '@/assets/icons/icon-add.svg?react';
import IconArrowRight from '@/assets/icons/icon-arrow-right.svg?react';

import ImageAddBlankSection from '@/assets/images/sections/section-add-blank-section.svg';
import ImageSavedSection from '@/assets/images/sections/section-saved-section.svg';
import ImageSectionTeam from '@/assets/images/sections/section-team.svg';
import ImageSectionCta from '@/assets/images/sections/section-cta.svg';
import ImageSectionContactForm from '@/assets/images/sections/section-contact-form.svg';
import ImageSectionProductShowcase from '@/assets/images/sections/section-product-showcase.svg';
import ImageSectionFaq from '@/assets/images/sections/section-faq.svg';
import ImageSectionFeatures from '@/assets/images/sections/section-features-overview.svg';
import ImageSectionHeader from '@/assets/images/sections/section-navigation-bar.svg';
import ImageSectionHero from '@/assets/images/sections/section-hero.svg';
import ImageSectionLogos from '@/assets/images/sections/section-stats-social-proof.svg';
import ImageSectionOptInForm from '@/assets/images/sections/section-newsletter-signup.svg';
import ImageSectionPricing from '@/assets/images/sections/section-pricing-plans.svg';
import ImageSectionSchedule from '@/assets/images/sections/section-comparison-table.svg';
import ImageSectionTestimonials from '@/assets/images/sections/section-testimonals.svg';
import {
  useEditor,
  Element as CraftElement,
  type NodeTree,
  type SerializedNodes,
} from '@craftjs/core';
import EditorContainer from '@/components/userComponents/Container';
import {
  ASSETS_PANEL,
  SECTION_CATEGORIES,
  normalizeSectionGroupId,
} from '@builder4/shared/utils/constants';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchSections } from '@/store/section/thunks';
import { selectSectionList, selectDefaultActiveCategoryId } from '@/store/section/selectors';
import { setDefaultActiveCategoryId } from '@/store/section/slice';
import { useCraftHelper } from '@/hooks/useCraftHelper';
import { parseOwnerIds } from '@/utils/sectionLibrary';

interface SectionProps {
  className: string;
  onSectionClick?: (sectionName: string) => void;
}

const SECTION_LIBRARY_CATEGORY_ICONS: Record<string, string> = {
  [SECTION_CATEGORIES.about.value]: ImageSectionTeam,
  [SECTION_CATEGORIES.cta.value]: ImageSectionCta,
  [SECTION_CATEGORIES.contact.value]: ImageSectionContactForm,
  [SECTION_CATEGORIES.event.value]: ImageSectionProductShowcase,
  [SECTION_CATEGORIES.faq.value]: ImageSectionFaq,
  [SECTION_CATEGORIES.features.value]: ImageSectionFeatures,
  [SECTION_CATEGORIES.header.value]: ImageSectionHeader,
  [SECTION_CATEGORIES.hero.value]: ImageSectionHero,
  [SECTION_CATEGORIES.logos.value]: ImageSectionLogos,
  [SECTION_CATEGORIES['opt-in-form'].value]: ImageSectionOptInForm,
  [SECTION_CATEGORIES.pricing.value]: ImageSectionPricing,
  [SECTION_CATEGORIES.schedule.value]: ImageSectionSchedule,
  [SECTION_CATEGORIES.testimonials.value]: ImageSectionTestimonials,
  // WHY: Placeholder — replace with a dedicated popup icon (section-popup.svg) when available
  [SECTION_CATEGORIES.popup.value]: ImageSectionCta,
};

export const Section: React.FC<SectionProps> = ({ className, onSectionClick }) => {
  const { getSectionNodeTreeWithUniqueIds, addSectionToRoot } = useCraftHelper();
  const dispatch = useAppDispatch();
  const sections = useAppSelector(selectSectionList);
  const defaultActiveCategoryId = useAppSelector(selectDefaultActiveCategoryId);
  const libraryOwnerIds = useMemo(
    () => parseOwnerIds(import.meta.env.VITE_SECTION_LIBRARY_OWNER_ID),
    [],
  );
  const { connectors, actions, query, selected } = useEditor((_, query) => ({
    selected: query.getEvent('selected').first(),
  }));
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(defaultActiveCategoryId);
  const [searchTerm, setSearchTerm] = useState('');

  // When a default category was set before opening the panel, apply it and clear so it's one-time.
  useEffect(() => {
    if (defaultActiveCategoryId != null) {
      setActiveCategoryId(defaultActiveCategoryId === '-1' ? null : defaultActiveCategoryId);
      setSearchTerm('');
      dispatch(setDefaultActiveCategoryId(null));
    }
  }, [defaultActiveCategoryId, dispatch]);

  useEffect(() => {
    dispatch(fetchSections());
  }, [dispatch]);

  const handleClick = (sectionName: string) => {
    if (onSectionClick) {
      onSectionClick(sectionName);
    }
  };

  const handleAddSectionToRoot = (nodeTree: NodeTree) => {
    addSectionToRoot(nodeTree, selected ?? null);
  };

  const handleSectionSelect = (sectionData: { rootNodeId: string; nodes: SerializedNodes }) => {
    const newTree = getSectionNodeTreeWithUniqueIds(sectionData);
    handleAddSectionToRoot(newTree);
  };

  const handleAddBlankSection = () => {
    const nodeTree = query
      .parseReactElement(<CraftElement canvas is={EditorContainer} />)
      .toNodeTree();
    handleAddSectionToRoot(nodeTree);
  };

  const activeCategory = activeCategoryId
    ? Object.values(SECTION_CATEGORIES).find((category) => category.value === activeCategoryId) ||
      null
    : null;

  // Section library categories are available only when a library owner allowlist is configured.
  const sectionLibraryEnabled = libraryOwnerIds.length > 0;
  const sectionLibraryCategories = useMemo(
    () => (sectionLibraryEnabled ? Object.values(SECTION_CATEGORIES) : []),
    [sectionLibraryEnabled],
  );
  // Visible categories are filtered by the current library search term.
  const visibleSectionLibraryCategories = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return sectionLibraryCategories;
    return sectionLibraryCategories.filter((category) =>
      category.label.toLowerCase().includes(normalizedSearch),
    );
  }, [sectionLibraryCategories, searchTerm]);

  // Published library sections are filtered by owner allowlist, selected category, and search term.
  const librarySectionsForCategory = useMemo(() => {
    if (!sectionLibraryEnabled || !activeCategoryId) return [];
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return sections.filter((section) => {
      if (!section.published) return false;
      if (!libraryOwnerIds.includes(section.ownerId)) return false;
      if (normalizeSectionGroupId(section.sectionGroupId) !== activeCategoryId) return false;
      if (!normalizedSearch) return true;
      return section.name?.toLowerCase().includes(normalizedSearch) ?? false;
    });
  }, [activeCategoryId, libraryOwnerIds, searchTerm, sectionLibraryEnabled, sections]);

  return (
    <div className={clsx(className, 'flex h-full max-w-70 flex-col')}>
      <PanelTopHeader
        className=""
        text={activeCategory?.label ?? 'Sections'}
        backButton={Boolean(activeCategory)}
        onClickClose={() => handleClick('')}
        onClickBtnBack={() => {
          setActiveCategoryId(null);
          setSearchTerm('');
        }}
      />

      {sectionLibraryEnabled ? (
        <PanelTopSearch
          className=""
          placeholder={
            activeCategory ? `Search ${activeCategory.label.toLowerCase()}...` : 'Search section...'
          }
          text=""
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          onSearch={(value: string) => setSearchTerm(value)}
        />
      ) : null}

      <ScrollArea className="flex w-full flex-1 gap-1 overflow-hidden px-6">
        <div className="flex flex-col gap-4 py-4">
          {!activeCategory ? (
            <>
              <div className="flex flex-col gap-1">
                <SectionField
                  className=""
                  text="Add Blank Section"
                  imageSrc={ImageAddBlankSection}
                  icon={IconAdd}
                  data-qa-selector="section-add-blank-button"
                  onClick={handleAddBlankSection}
                />

                <SectionField
                  className=""
                  text="Saved Sections"
                  imageSrc={ImageSavedSection}
                  icon={IconArrowRight}
                  data-qa-selector="section-saved-sections-button"
                  onClick={() => handleClick(ASSETS_PANEL.SAVED_SECTIONS)}
                />
              </div>

              {sectionLibraryEnabled ? (
                <>
                  <hr className="my-1 w-full border-neutral-100 bg-neutral-100" />
                  <div className="flex flex-col gap-1">
                    {visibleSectionLibraryCategories.map((category) => (
                      <SectionField
                        key={category.value}
                        className=""
                        text={category.label}
                        imageSrc={SECTION_LIBRARY_CATEGORY_ICONS[category.value]}
                        icon={IconArrowRight}
                        data-qa-selector={`section-category-${category.value}-button`}
                        onClick={() => {
                          setActiveCategoryId(category.value);
                          setSearchTerm('');
                        }}
                      />
                    ))}
                  </div>
                </>
              ) : null}
            </>
          ) : (
            <div className="flex flex-col gap-3">
              {librarySectionsForCategory.length === 0 ? (
                <p className="text-button-sm text-center text-neutral-500">No sections yet</p>
              ) : (
                librarySectionsForCategory.map(({ id, name, craftJson }) => (
                  <SectionFieldSaved
                    key={id}
                    title={name}
                    hideTitle={true}
                    highlightOnHover={true}
                    sectionData={craftJson}
                    data-qa-selector={`section-library-${id}-item`}
                    onClick={() => handleSectionSelect(craftJson)}
                    ref={(ref) => {
                      if (ref) {
                        connectors.create(ref, () => getSectionNodeTreeWithUniqueIds(craftJson), {
                          onCreate: (nodeTree) => {
                            actions.selectNode(nodeTree.rootNodeId);
                          },
                        });
                      }
                    }}
                  />
                ))
              )}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
